// Contraction topic: session building + the preposition × article reference
// table. Pure (no React) so the interleaving rules can be unit-tested.
//
// Brazilian Portuguese only. Four required-contraction categories (a, de, em,
// por) plus "para", whose pro/pra forms are colloquial (speech and casual
// writing), not mandatory orthography. No definiteness axis — each category
// carries whichever definite/indefinite forms are actually common. "dum/duma"
// is deliberately absent. A second group, preposition + pronoun, covers the two
// contractions that exist with ele/ela/eles/elas: de (dele…) and em (nele…).
// "a ele" and "por ele" never contract, so there is no a/por pronoun category.

import contractionData from '../../data/contractions.json'
import { shuffle } from './phraseBank'

export type ContractionGroup = 'article' | 'pronoun'

export type ContractionCategory = {
  id: string
  prep: string
  group: ContractionGroup
  label: string
  forms: string
  defaultOn: boolean
}

export type ContractionItem = {
  prep: string
  // The category this item belongs to. Absent for the preposition + article
  // items, whose category id is the preposition itself.
  category?: string
  // The article or demonstrative; for the pronoun categories, the pronoun (ele…).
  article: string
  // Pronoun categories only: the referent in Portuguese, article included ("as meninas").
  // The sentence always names it, so the pronoun's gender and number never depend on the
  // English gloss, and the semi-guided hint shows it: (de + as meninas).
  referent?: string
  // Possession items only: the possessed noun ("a festa"). The blank holds the noun plus
  // the contraction ("A festa dela"), and the hint gives the parts in assembly order.
  noun?: string
  // Possession items that share a possessor but differ in the gender of the possessed
  // noun (o carro dela / a casa dela) carry the same pair id.
  pair?: string
  answer: string
  // Grammatical sub-type the item belongs to (English label). Drives the
  // "mix: …" descriptor on the setup screen.
  type: string
  sentence: string
  translation: string
  rule: string
}

export const CONTRACTION_CATEGORIES = contractionData.categories as ContractionCategory[]
export const DEFAULT_CONTRACTION_IDS = CONTRACTION_CATEGORIES.filter((category) => category.defaultOn).map((category) => category.id)
export const CONTRACTION_ITEMS = contractionData.items as ContractionItem[]

export function categoryOf(item: ContractionItem): string {
  return item.category ?? item.prep
}

// Fixed session length (PRD §5): not user-configurable.
export const CONTRACTION_SESSION_LENGTH = 10

// Preferred display order of sub-types per preposition. The descriptor only
// ever shows sub-types that actually appear in that preposition's item pool —
// this list just orders them. A sub-type present in the pool but missing here
// still shows, appended after the ordered ones.
const SUBTYPE_ORDER: Record<string, string[]> = {
  'de-pronoun': ['possession', 'verbs', 'position'],
  'em-pronoun': ['location', 'time', 'verbs'],
  de: ['possession', 'position', 'verbs'],
  em: ['location', 'time', 'verbs'],
  a: ['direction', 'to person', 'time', 'verbs'],
  por: ['through', 'cause', 'exchange', 'verbs'],
  para: ['direction', 'purpose', 'time'],
}

// Every sub-type actually drilled for a preposition, in display order. The
// catch-all "verbs" bucket renders as "verb + <prep>" so it's clear the
// preposition is the one a set of verbs governs (gostar de, pensar em, …).
export function contractionSubtypes(categoryId: string): string[] {
  const prep = CONTRACTION_CATEGORIES.find((category) => category.id === categoryId)?.prep ?? categoryId
  const present = new Set(CONTRACTION_ITEMS.filter((item) => categoryOf(item) === categoryId).map((item) => item.type))
  const ordered = (SUBTYPE_ORDER[categoryId] ?? []).filter((subtype) => present.has(subtype))
  for (const subtype of present) if (!ordered.includes(subtype)) ordered.push(subtype)
  return ordered.map((subtype) => (subtype === 'verbs' ? `verb + ${prep}` : subtype))
}

// ── Reference table: every preposition × article combination -------------------

export type ArticleKind = 'definite' | 'indefinite' | 'demonstrative'
export type ContractionRow = {
  article: string
  kind: ArticleKind
  gender: string
  // Contracted form per preposition, or '—' where the pair does not contract
  // (and is not commonly written as two words) in Brazilian Portuguese.
  forms: Record<string, string>
}

export const CONTRACTION_TABLE: ContractionRow[] = [
  { article: 'o', kind: 'definite', gender: 'm. sing.', forms: { de: 'do', em: 'no', a: 'ao', por: 'pelo', para: 'pro' } },
  { article: 'a', kind: 'definite', gender: 'f. sing.', forms: { de: 'da', em: 'na', a: 'à', por: 'pela', para: 'pra' } },
  { article: 'os', kind: 'definite', gender: 'm. pl.', forms: { de: 'dos', em: 'nos', a: 'aos', por: 'pelos', para: 'pros' } },
  { article: 'as', kind: 'definite', gender: 'f. pl.', forms: { de: 'das', em: 'nas', a: 'às', por: 'pelas', para: 'pras' } },
  { article: 'um', kind: 'indefinite', gender: 'm. sing.', forms: { de: 'de um', em: 'num', a: '—', por: '—', para: '—' } },
  { article: 'uma', kind: 'indefinite', gender: 'f. sing.', forms: { de: 'de uma', em: 'numa', a: '—', por: '—', para: '—' } },
  { article: 'uns', kind: 'indefinite', gender: 'm. pl.', forms: { de: 'de uns', em: 'nuns', a: '—', por: '—', para: '—' } },
  { article: 'umas', kind: 'indefinite', gender: 'f. pl.', forms: { de: 'de umas', em: 'numas', a: '—', por: '—', para: '—' } },
  // Demonstratives contract with de/em (and, for the aquele series only, with
  // a — àquele etc., since "a este/esse" stays two words). None contract with
  // por/para.
  { article: 'este', kind: 'demonstrative', gender: 'm. sing.', forms: { de: 'deste', em: 'neste', a: '—', por: '—', para: '—' } },
  { article: 'esta', kind: 'demonstrative', gender: 'f. sing.', forms: { de: 'desta', em: 'nesta', a: '—', por: '—', para: '—' } },
  { article: 'estes', kind: 'demonstrative', gender: 'm. pl.', forms: { de: 'destes', em: 'nestes', a: '—', por: '—', para: '—' } },
  { article: 'estas', kind: 'demonstrative', gender: 'f. pl.', forms: { de: 'destas', em: 'nestas', a: '—', por: '—', para: '—' } },
  { article: 'isto', kind: 'demonstrative', gender: 'neuter', forms: { de: 'disto', em: 'nisto', a: '—', por: '—', para: '—' } },
  { article: 'esse', kind: 'demonstrative', gender: 'm. sing.', forms: { de: 'desse', em: 'nesse', a: '—', por: '—', para: '—' } },
  { article: 'essa', kind: 'demonstrative', gender: 'f. sing.', forms: { de: 'dessa', em: 'nessa', a: '—', por: '—', para: '—' } },
  { article: 'esses', kind: 'demonstrative', gender: 'm. pl.', forms: { de: 'desses', em: 'nesses', a: '—', por: '—', para: '—' } },
  { article: 'essas', kind: 'demonstrative', gender: 'f. pl.', forms: { de: 'dessas', em: 'nessas', a: '—', por: '—', para: '—' } },
  { article: 'isso', kind: 'demonstrative', gender: 'neuter', forms: { de: 'disso', em: 'nisso', a: '—', por: '—', para: '—' } },
  { article: 'aquele', kind: 'demonstrative', gender: 'm. sing.', forms: { de: 'daquele', em: 'naquele', a: 'àquele', por: '—', para: '—' } },
  { article: 'aquela', kind: 'demonstrative', gender: 'f. sing.', forms: { de: 'daquela', em: 'naquela', a: 'àquela', por: '—', para: '—' } },
  { article: 'aqueles', kind: 'demonstrative', gender: 'm. pl.', forms: { de: 'daqueles', em: 'naqueles', a: 'àqueles', por: '—', para: '—' } },
  { article: 'aquelas', kind: 'demonstrative', gender: 'f. pl.', forms: { de: 'daquelas', em: 'naquelas', a: 'àquelas', por: '—', para: '—' } },
  { article: 'aquilo', kind: 'demonstrative', gender: 'neuter', forms: { de: 'daquilo', em: 'naquilo', a: 'àquilo', por: '—', para: '—' } },
]

export const CONTRACTION_PREPS = ['de', 'em', 'a', 'por', 'para'] as const

// Preposition + pronoun: only de and em contract, with no exceptions. a ele and
// por ele stay two words, so they are deliberately not listed.
export const PRONOUN_CONTRACTIONS: Record<string, Record<string, string>> = {
  ele: { de: 'dele', em: 'nele' },
  ela: { de: 'dela', em: 'nela' },
  eles: { de: 'deles', em: 'neles' },
  elas: { de: 'delas', em: 'nelas' },
}

// com + pronoun (Brazilian forms): four merge into one word, the rest stay two words. Reference only,
// not part of the practice drill. `pair` is the feminine half of a folded column (ele / ela).
export type ComPronounForm = { pronoun: string; pair?: string; gloss: string; form: string }

export const COM_PRONOUN_FORMS: ComPronounForm[] = [
  { pronoun: 'eu', gloss: 'with me', form: 'comigo' },
  { pronoun: 'tu', gloss: 'with you', form: 'contigo' },
  { pronoun: 'ele', pair: 'ela', gloss: 'with himself / herself', form: 'consigo' },
  { pronoun: 'nós', gloss: 'with us', form: 'conosco' },
]

// The ones that do not merge: com + the pronoun, two words. [form, English]
export const COM_TWO_WORD_FORMS: Array<[string, string]> = [
  ['com ele', 'with him'],
  ['com ela', 'with her'],
  ['com você', 'with you'],
  ['com vocês', 'with you all'],
  ['com eles', 'with them'],
  ['com elas', 'with them, f.'],
]

// The expected written form for one preposition + article (or pronoun) pair.
export function contractionForm(prep: string, article: string): string | undefined {
  return CONTRACTION_TABLE.find((row) => row.article === article)?.forms[prep] ?? PRONOUN_CONTRACTIONS[article]?.[prep]
}

export function articleGender(article: string): string {
  return CONTRACTION_TABLE.find((row) => row.article === article)?.gender ?? ''
}

// ── Session building ---------------------------------------------------------

// How much of the preposition + article breakdown to show beside the blank.
// `full` tests only contraction spelling; `prep` also tests article agreement;
// `none` also tests preposition choice.
export type HintLevel = 'full' | 'prep' | 'none'
export const DEFAULT_HINT_LEVEL: HintLevel = 'prep'

export type ContractionPrompt = {
  item: ContractionItem
  beforeWords: string[]
  afterWords: string[]
  sentence: string
  cueText: string // '' means no parenthetical is shown at all
}

// The parenthetical contents for a given hint level ('' → render nothing). The challenge level
// governs the article cards only. The pronoun cards always show the Portuguese referent, whatever
// level is selected, because the gender and number of "them" can't be read from the English:
// (de + as meninas), and a possession cue leads with the possessed noun: (a festa + de + a Maria).
export function cueFor(item: ContractionItem, hint: HintLevel): string {
  if (item.referent !== undefined) {
    return item.noun ? `${item.noun} + ${item.prep} + ${item.referent}` : `${item.prep} + ${item.referent}`
  }
  if (hint === 'none') return ''
  if (hint === 'prep') return `${item.prep} + ___`
  return `${item.prep} + ${item.article}`
}

// ── Card selection: one group at a time ---------------------------------------
// The article cards and the pronoun cards run on different practice formats, so checking a card in
// one group clears the other.
function groupOf(id: string): ContractionGroup | undefined {
  return CONTRACTION_CATEGORIES.find((category) => category.id === id)?.group
}

export function toggleContractionCard(ids: string[], id: string): string[] {
  if (ids.includes(id)) return ids.filter((existing) => existing !== id)
  const group = groupOf(id)
  return [...ids.filter((existing) => groupOf(existing) === group), id]
}

// How many prompts the selected cards hold.
export function contractionPromptCount(ids: string[]): number {
  return CONTRACTION_ITEMS.filter((item) => ids.includes(categoryOf(item))).length
}

// True when every selected card is a pronoun card: the challenge level doesn't apply, so the
// selector is hidden.
export function onlyPronounCards(ids: string[]): boolean {
  return ids.length > 0 && ids.every((id) => groupOf(id) === 'pronoun')
}

const normalizeAnswer = (text: string) =>
  text
    .normalize('NFC')
    .toLocaleLowerCase('pt')
    .replace(/[.!?,;:]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()

export const POSSESSION_ORDER_NOTE = 'The dela-phrase always follows the noun — o nome dela, never dela o nome.'

// "a Maria" → "da Maria": the referent written out with the contraction, which is correct
// Portuguese but not what the pronoun drill asks for.
function referentWithContraction(item: ContractionItem): string | null {
  if (!item.referent) return null
  const [article, ...rest] = item.referent.split(' ')
  const contracted = contractionForm(item.prep, article)
  return contracted ? [contracted, ...rest].join(' ') : null
}

// One line of feedback for a wrong possession answer, or null. The right words in the wrong
// order get the ordering rule; the noun + referent written out gets a nudge toward the pronoun.
export function possessionNote(item: ContractionItem, typed: string): string | null {
  if (!item.noun) return null
  const given = normalizeAnswer(typed)
  const target = normalizeAnswer(item.answer)
  if (!given || given === target) return null
  const sorted = (text: string) => text.split(' ').sort().join(' ')
  if (sorted(given) === sorted(target)) return POSSESSION_ORDER_NOTE
  const written = referentWithContraction(item)
  if (written && given === normalizeAnswer(`${item.noun} ${written}`)) {
    return `"${item.noun} ${written}" is correct Portuguese too — this drill wants the pronoun: ${item.noun} ${item.answer.split(' ').pop()}.`
  }
  return null
}

function toPrompt(item: ContractionItem, hint: HintLevel): ContractionPrompt {
  const [before, after] = item.sentence.split('___')
  return {
    item,
    beforeWords: before.trim().split(/\s+/).filter(Boolean),
    afterWords: after.trim().split(/\s+/).filter(Boolean),
    sentence: `${before}${item.answer}${after}`,
    cueText: cueFor(item, hint),
  }
}

// Interleave selected categories into `length` items. One category → all from
// it. Two or more → random draw that never repeats a category back-to-back
// while another is available, and isn't a fixed round-robin (PRD §4).
function interleave(pools: Map<string, ContractionItem[]>, length: number): ContractionItem[] {
  const out: ContractionItem[] = []
  let previous: string | null = null

  while (out.length < length) {
    const available = [...pools.entries()].filter(([, items]) => items.length > 0)
    if (available.length === 0) break
    let choices = available.filter(([id]) => id !== previous)
    if (choices.length === 0) choices = available
    const [id, items] = choices[Math.floor(Math.random() * choices.length)]
    out.push(items.pop() as ContractionItem)
    previous = id
  }

  return out
}

export function buildContractionSession(selectedIds: string[], hint: HintLevel = DEFAULT_HINT_LEVEL): ContractionPrompt[] {
  const active = selectedIds.length > 0 ? selectedIds : DEFAULT_CONTRACTION_IDS
  const pools = new Map<string, ContractionItem[]>()
  for (const id of active) {
    const pool = shuffle(CONTRACTION_ITEMS.filter((item) => categoryOf(item) === id))
    if (pool.length > 0) pools.set(id, pool)
  }
  return interleave(pools, CONTRACTION_SESSION_LENGTH).map((item) => toPrompt(item, hint))
}
